import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, LOGO_URL, inr } from "@/lib/api";
import { PriceTag } from "@/components/PriceTag";
import { PageBackground } from "@/components/PageBackground";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Award, Download, ArrowRight, CheckCircle2 } from "lucide-react";

const WHY = [
  "Fixed departure dates with confirmed group seats",
  "Hotels close to Masjid al-Haram and Masjid an-Nabawi",
  "Experienced Muallems guiding every group",
  "Transparent pricing with no hidden charges",
  "Complete visa, flight, transport and Ziyarat support",
  "Dedicated customer care on WhatsApp throughout your journey",
];

const STEPS = [
  "Contact our office or any Associate Partner on WhatsApp.",
  "Choose your package and confirmed departure date.",
  "Submit your passport and photos; we handle the visa.",
  "Pay in easy instalments as guided by our team.",
  "Collect your travel kit and depart with your group, Insha'Allah.",
];

export default function Prospectus() {
  const [data, setData] = useState({ packages: [], muallems: [], offices: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/public/prospectus`).then((r) => r.json()).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="relative min-h-screen" data-testid="prospectus-page">
      <PageBackground />
      <header className="h-16 flex items-center justify-between px-4 sm:px-6 border-b border-border/60 glass sticky top-0 z-30">
        <Link to="/showcase" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Bengal Umrah House" className="w-10 h-10 rounded-full ring-1 ring-gold/50" />
          <div>
            <p className="font-serif text-sm font-semibold">Bengal Umrah House</p>
            <p className="eyebrow text-gold/80">Prospectus & Guide</p>
          </div>
        </Link>
        <a href={`${API}/public/prospectus.pdf`} target="_blank" rel="noopener noreferrer">
          <Button size="sm" data-testid="download-prospectus-btn" className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-1.5 text-xs">
            <Download className="w-3.5 h-3.5" /> Download PDF
          </Button>
        </a>
      </header>

      <section className="brand-gradient px-4 sm:px-6 py-14 text-center">
        <p className="eyebrow text-gold mb-3 tracking-[0.3em]">Labbaik Allahumma Labbaik</p>
        <h1 className="font-serif text-3xl sm:text-5xl font-semibold text-white">Company Prospectus & Pilgrim Guide</h1>
        <p className="text-emerald-100/70 text-sm sm:text-base mt-4 max-w-2xl mx-auto leading-relaxed">
          Everything about Bengal Umrah House — our packages, departure dates, group leaders and offices. This guide updates automatically with our latest packages and Muallems.
        </p>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12 space-y-12">
        {loading && <p className="text-sm text-muted-foreground text-center py-12">Loading prospectus…</p>}

        <section data-testid="prospectus-about">
          <h2 className="font-serif text-2xl font-semibold text-gold mb-3">About Bengal Umrah House</h2>
          <p className="text-emerald-100/80 text-sm leading-relaxed">
            Bengal Umrah House is a trusted Umrah, Hajj and Ziyarat travel organisation dedicated to serving pilgrims with care, honesty and complete guidance. From visa processing and flights to hotels near the Haram, transport and experienced group leaders (Muallems), we handle every detail of your sacred journey so you can focus on your ibadah.
          </p>
        </section>

        <section data-testid="prospectus-why">
          <h2 className="font-serif text-2xl font-semibold text-gold mb-4">Why Choose Us</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {WHY.map((w, i) => (
              <div key={i} className="flex items-start gap-2.5 text-sm text-emerald-100/80"><CheckCircle2 className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" /> {w}</div>
            ))}
          </div>
        </section>

        <section data-testid="prospectus-packages">
          <h2 className="font-serif text-2xl font-semibold text-gold mb-4">Our Umrah & Hajj Packages</h2>
          {data.packages.length === 0 && !loading && <p className="text-sm text-muted-foreground">Package details will be shared by our office. Please contact us on WhatsApp.</p>}
          <div className="grid sm:grid-cols-2 gap-4">
            {data.packages.map((p) => (
              <Card key={p.id} className="p-5 bg-card/40 border-border/60">
                <p className="font-serif font-semibold text-white">{p.name}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{p.desc}</p>
                <p className="mt-3"><PriceTag price={p.price} mrp={p.mrp} until={p.offer_until} big /><span className="text-[10px] text-muted-foreground"> / person</span></p>
              </Card>
            ))}
          </div>
        </section>

        <section data-testid="prospectus-muallems">
          <h2 className="font-serif text-2xl font-semibold text-gold mb-4">Your Group Leaders (Muallems)</h2>
          {data.muallems.length === 0 && !loading && <p className="text-sm text-muted-foreground">Our experienced Muallems accompany every group. Details available at our office.</p>}
          <div className="grid sm:grid-cols-2 gap-4">
            {data.muallems.map((m) => (
              <Card key={m.id} className="p-4 bg-card/40 border-border/60 flex items-start gap-4">
                {m.has_photo ? (
                  <img src={`${API}/public/muallems/${m.id}/photo`} alt={m.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-gold/50 flex-shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-primary/50 border border-gold/30 flex items-center justify-center text-xl font-serif text-gold flex-shrink-0">{m.name[0]}</div>
                )}
                <div className="min-w-0">
                  <p className="font-serif font-semibold text-white">{m.name}</p>
                  {m.experience && <p className="text-xs text-muted-foreground mt-1 flex items-start gap-1.5"><Award className="w-3.5 h-3.5 text-gold mt-0.5 flex-shrink-0" />{m.experience}</p>}
                  {m.phone && <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><Phone className="w-3 h-3 text-gold" /> {m.phone}</p>}
                </div>
              </Card>
            ))}
          </div>
        </section>

        <section data-testid="prospectus-booking">
          <h2 className="font-serif text-2xl font-semibold text-gold mb-4">How to Book</h2>
          <ol className="space-y-3">
            {STEPS.map((s, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-emerald-100/80">
                <span className="w-6 h-6 rounded-full bg-gold/15 border border-gold/40 text-gold text-xs flex items-center justify-center flex-shrink-0 font-mono">{i + 1}</span> {s}
              </li>
            ))}
          </ol>
        </section>

        <section data-testid="prospectus-offices">
          <h2 className="font-serif text-2xl font-semibold text-gold mb-4">Our Offices</h2>
          {data.offices.length === 0 && !loading && <p className="text-sm text-muted-foreground">Please contact us on WhatsApp for our office address.</p>}
          <div className="grid sm:grid-cols-2 gap-4">
            {data.offices.map((o) => (
              <Card key={o.id} className="p-4 bg-card/40 border-border/60">
                <p className="font-serif font-semibold text-white flex items-center gap-1.5"><MapPin className="w-4 h-4 text-gold" /> {o.name}</p>
                {o.address && <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{o.address}{o.city ? `, ${o.city}` : ""}</p>}
                {o.phone && <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><Phone className="w-3 h-3 text-gold" /> {o.phone}</p>}
              </Card>
            ))}
          </div>
        </section>

        <div className="text-center pt-4">
          <Link to="/showcase">
            <Button data-testid="prospectus-back-btn" variant="outline" className="border-gold/40 text-gold hover:bg-gold/10 gap-2">Back to Website <ArrowRight className="w-4 h-4" /></Button>
          </Link>
        </div>
        <p className="text-center text-[11px] text-muted-foreground/60 pb-8">© Bengal Umrah House Travels — All journeys planned with care.</p>
      </div>
    </div>
  );
}
